// gtmHelper.ts

'use client';

import { sendGTMEvent as NextSendGTMEvent } from '@next/third-parties/google';

import type { AIReviewStatus } from '@/types/ai-review-status';
import type { ContentUnitType } from '@/types/unit';
import type { AutocourseReviewFormFields } from '@/validations';

export enum GTMEventType {
  UPLOAD_AUTOCOURSE = 'upload_doc2course',
  GENERATE_FROM_AUTOCOURSE_STRUCTURE = 'generate_from_doc2course_structure',
  CERTIFICATE_CSV_DOWNLOADED = 'certificate_csv_downloaded',
  CERTIFICATE_PDF_DOWNLOADED = 'certificate_pdf_downloaded',
  CERTIFICATE_PDF_PREVIEWED = 'certificate_pdf_opened',
  AUTOCOURSE_SUMMARY_CHANGES = 'doc2course_summary_changes',
  ANSWER_CREATED = 'answer_created',
  ANSWER_DELETED = 'answer_deleted',
  ANSWER_EDITED = 'answer_edited',
  QUESTION_CREATED = 'question_created',
  QUESTION_DELETED = 'question_deleted',
  QUESTION_EDITED = 'question_edited',
  QUIZ_AUTOGEN_PUBLISHED = 'quiz_autogen_published',
  QUIZ_AUTOGEN_SAVED = 'quiz_autogen_saved',
  QUIZ_PUBLISHED = 'quiz_published',
  QUIZ_UNPUBLISHED = 'quiz_unpublished',
  QUIZ_TRANSLATION_SUCCEEDED = 'quiz_translation_succeeded',
  QUIZ_VIEWED = 'quiz_viewed',
  IMAGE_SELECTED = 'image_selected',
  VIDEO_UPLOADED = 'video_uploaded',
  IMAGE_UPLOADED = 'image_uploaded',
  IMAGE_ARCHIVED = 'image_archived',
  SEQUENCE_CONTENT_CHANGED = 'sequence_content_changed',
  STEP_CREATED = 'step_created',
  STEP_DELETED = 'step_deleted',
  STEP_EDITED = 'step_edited',
  SEQUENCE_PUBLISHED = 'sequence_published',
  SEQUENCE_UNPUBLISHED = 'sequence_unpublished',
  SEQUENCE_TRANSLATION_SUCCEEDED = 'sequence_translation_succeeded',
  SEQUENCE_TRANSLATION_STARTED = 'sequence_translation_started',
  SEQUENCE_TRANSLATION_CANCELLED = 'sequence_translation_cancelled',
  SEQUENCE_TRANSLATION_UPDATES_SUCCEEDED = 'sequence_translation_updates_succeeded',
  SEQUENCE_TRANSLATION_UPDATES_VIEWED = 'sequence_translation_updates_viewed',
  SEQUENCE_DUPLICATED = 'sequence_duplicated',
  SEQUENCE_VIEWED = 'sequence_viewed',
  SEQUENCE_LANGUAGE_ADD_STARTED = 'sequence_language_add_started',
  SEQUENCE_LANGUAGE_NAVIGATION_STARTED = 'sequence_language_navigation_started',
  SEQUENCE_LANGUAGE_NAVIGATION_VIEWED = 'sequence_language_navigation_viewed',
  INFEE_TOGGLED = 'infee_toggled',
  INFEE_CLICKED = 'infee_clicked',
  COURSE_CREATED = 'course_created',
  COURSE_DELETED = 'course_deleted',
  COURSE_HANDOUT_DOWNLOADED = 'course_handout_downloaded',
  COURSE_METADATA_EDITED = 'course_metadata_edited',
  UNIT_CREATED = 'unit_created',
  UNIT_DELETED = 'unit_deleted',
  UNIT_EDITED = 'unit_edited',
  COURSES_COPY_TRIGGERED = 'courses_copy_triggered',
  // Add more event types as needed
}

// Define the payload types for each event type
type EventPayloads = {
  [GTMEventType.UPLOAD_AUTOCOURSE]: {
    size: number;
    type: string;
  };

  [GTMEventType.GENERATE_FROM_AUTOCOURSE_STRUCTURE]: {
    original_course_structure: AutocourseReviewFormFields;
    updated_course_structure: AutocourseReviewFormFields;
  };

  [GTMEventType.CERTIFICATE_CSV_DOWNLOADED]: {
    file_name: string;
  };
  [GTMEventType.CERTIFICATE_PDF_DOWNLOADED]: {
    pdf_language: string;
    certificate_id: string;
  };
  [GTMEventType.CERTIFICATE_PDF_PREVIEWED]: {
    pdf_language: string;
    certificate_id: string;
  };
  [GTMEventType.AUTOCOURSE_SUMMARY_CHANGES]: {
    course_structure_id?: string;
    summary_changes: {
      sequenceInfoId: string;
      originalSummary?: string;
      newSummary?: string;
    }[];
  };

  [GTMEventType.ANSWER_CREATED]: {
    newAnswer_isCorrect: boolean;
    newAnswer_text: string;
    isAIgenerated?: AIReviewStatus;
  };
  [GTMEventType.ANSWER_DELETED]: {
    oldAnswer_isCorrect: boolean;
    oldAnswer_text: string;
    isAIgenerated?: AIReviewStatus;
  };
  [GTMEventType.ANSWER_EDITED]: {
    newAnswer_isCorrect: boolean;
    newAnswer_text: string;
    oldAnswer_isCorrect: boolean;
    oldAnswer_text: string;
    isAIgenerated?: AIReviewStatus;
  };
  [GTMEventType.QUESTION_CREATED]: {
    newQuestion_text: string;
    isAIgenerated?: AIReviewStatus;
  };
  [GTMEventType.QUESTION_DELETED]: {
    oldQuestion_text: string;
    isAIgenerated?: AIReviewStatus;
  };
  [GTMEventType.QUESTION_EDITED]: {
    oldQuestion_text: string;
    newQuestion_text: string;
    isAIgenerated?: AIReviewStatus;
  };
  [GTMEventType.QUIZ_AUTOGEN_PUBLISHED]: {
    quizAutoGenerationPublish: {
      sequence_id: string;
      language: string;
      trigger: string;
      quiz_id: string;
    };
  };
  [GTMEventType.QUIZ_AUTOGEN_SAVED]: {
    quizAutoGenerationSave: {
      sequence_id: string;
      language: string;
      quiz_id: string;
    };
  };
  [GTMEventType.QUIZ_PUBLISHED]: {
    publish: {
      trigger: 'publish_button' | 'publish_all';
      location: 'course_edit' | 'quiz_edit';
      unitStatusBeforeSave: 'published' | 'unpublished' | 'unknown';
      isAIgenerated?: AIReviewStatus;
    };
  };
  [GTMEventType.QUIZ_UNPUBLISHED]: {
    publish: {
      trigger: 'unpublish_button';
      location: 'course_edit' | 'quiz_edit';
      unitStatusBeforeSave: 'published' | 'unpublished' | 'unknown';
    };
  };
  [GTMEventType.QUIZ_TRANSLATION_SUCCEEDED]: {
    quizTranslationSuccess: {
      quiz_id: string;
      current_cms_language: string;
      language: string;
      number_of_questions: number;
    };
  };
  [GTMEventType.QUIZ_VIEWED]: {
    quizView: {
      quiz_id: string;
      language: string;
      number_of_questions: number;
      current_cms_language: string;
    };
  };
  [GTMEventType.IMAGE_SELECTED]: {
    ImageSelected_Space: string;
    ImageSelected_Location: string;
    ImageSelected_recommendedImage: boolean;
  };
  [GTMEventType.VIDEO_UPLOADED]: {
    VideoUploaded_Location: string;
    VideoUploaded_Source: string;
    VideoUploaded_Size: number;
    VideoUploaded_Mime: string;
    VideoUploaded_Space: string;
    VideoUploaded_UploadDuration: number;
    VideoUploaded_Hash: string;
  };
  [GTMEventType.IMAGE_UPLOADED]: {
    imageUpload_location: string;
    imageUpload_source: string;
    imageUpload_size: number;
    imageUpload_mime: string;
    imageUpload_space: string;
    imageUpload_upload_duration: number;
    imageUpload_hash: string;
  };
  [GTMEventType.IMAGE_ARCHIVED]: {
    imageArchived_space: string;
    imageArchived_id: string;
    imageArchived_url: string;
  };
  // Add sequence-specific payload types
  [GTMEventType.SEQUENCE_CONTENT_CHANGED]: {
    oldTitle?: string;
    newTitle?: string;
    oldStepCount?: number;
    newStepCount?: number;
  };
  [GTMEventType.STEP_CREATED]: {
    newStep_id: string;
    newStep_text: string;
  };
  [GTMEventType.STEP_DELETED]: {
    oldStep_id: string;
    oldStep_text: string;
  };
  [GTMEventType.STEP_EDITED]: {
    step_id: string;
    oldStep_text: string;
    newStep_text: string;
  };
  [GTMEventType.SEQUENCE_PUBLISHED]: {
    publish: {
      trigger: 'publish_button' | 'publish_all';
      location: 'course_edit' | 'sequence_edit';
      unitStatusBeforeSave: 'published' | 'unpublished' | 'unknown';
    };
  };
  [GTMEventType.SEQUENCE_UNPUBLISHED]: {
    publish: {
      trigger: 'unpublish_button';
      location: 'course_edit' | 'sequence_edit';
      unitStatusBeforeSave: 'published' | 'unpublished' | 'unknown';
    };
  };
  [GTMEventType.SEQUENCE_TRANSLATION_SUCCEEDED]: {
    sequenceTranslationSuccess: {
      sequence_id: string;
      language: string;
      current_cms_language: string;
      number_of_steps: number;
    };
  };
  [GTMEventType.SEQUENCE_DUPLICATED]: {
    sequence_id: string;
    source_language: string;
    target_language: string;
  };
  [GTMEventType.SEQUENCE_VIEWED]: {
    sequenceView: {
      sequence_id: string;
      language: string;
      number_of_steps: number;
      current_cms_language: string;
    };
  };
  [GTMEventType.SEQUENCE_LANGUAGE_ADD_STARTED]: {
    sequenceLanguageAddStart: {
      sequence_id: string;
      number_of_steps: number;
      current_cms_language: string;
      selected_language: string;
    };
  };
  [GTMEventType.SEQUENCE_LANGUAGE_NAVIGATION_STARTED]: {
    sequenceLanguageNavigationStart: {
      source_sequence_id: string;
      target_sequence_id: string;
      number_of_steps: number;
      current_cms_language: string;
      selected_language: string;
    };
  };
  [GTMEventType.SEQUENCE_LANGUAGE_NAVIGATION_VIEWED]: {
    sequenceLanguageNavigationView: {
      id: string;
      number_of_existing_languages: number;
      number_of_addable_languages: number;
    };
  };
  [GTMEventType.SEQUENCE_TRANSLATION_STARTED]: {
    sequenceTranslationStart: {
      sequence_id: string;
      language: string;
      current_cms_language: string;
    };
  };
  [GTMEventType.SEQUENCE_TRANSLATION_CANCELLED]: {
    sequenceTranslationCancel: {
      sequence_id: string;
      source_language: string;
      target_language: string;
      current_cms_language: string;
      number_of_steps: number;
      reason_for_cancel: string;
    };
  };
  [GTMEventType.SEQUENCE_TRANSLATION_SUCCEEDED]: {
    sequenceTranslationSuccess: {
      sequence_id: string;
      language: string;
      current_cms_language: string;
      number_of_steps: number;
    };
  };
  [GTMEventType.SEQUENCE_TRANSLATION_UPDATES_SUCCEEDED]: {
    sequenceTranslationUpdatesSucceed: {
      language: string;
      sequence_id: string;
      target_languages: string[];
    };
  };
  [GTMEventType.SEQUENCE_TRANSLATION_UPDATES_VIEWED]: {
    sequenceTranslationUpdatesView: {
      sequence_id: string;
      source_language: string;
    };
  };
  [GTMEventType.INFEE_TOGGLED]: {
    infeeToggle: {
      sequence_id: string;
      current_sequence_language: string;
      changed_to: 'enabled' | 'disabled';
    };
  };
  [GTMEventType.INFEE_CLICKED]: {
    infeeClick: {
      sequence_id: string;
      current_sequence_language: string;
      step_id: string;
      step_number: number;
      rule_type: string;
      severity: string;
      highlight_title: string;
      highlight_text: string;
      highlight_begin: number;
      highlight_end: number;
    };
  };
  [GTMEventType.COURSE_CREATED]: {
    course_id: string;
    course_title: string;
    course_language: string;
    isAIgenerated?: AIReviewStatus;
  };
  [GTMEventType.COURSE_DELETED]: {
    course_id: string;
    course_titles: string[];
    course_languages: string[];
    isAIgenerated?: AIReviewStatus;
  };
  [GTMEventType.COURSE_HANDOUT_DOWNLOADED]: {
    course_id: string;
    course_title: string;
    handout_language: string;
  };
  [GTMEventType.COURSE_METADATA_EDITED]: {
    course_id: string;
    languages_changed?: string[];
    title_changed?: string[];
    language_visibility_changed?: string[];
    cover_image_changed?: boolean;
    video_changed?: string[];
    survey_changed?: string[];
    certificate_switched_to?: boolean;
    isAIgenerated?: AIReviewStatus;
  };
  [GTMEventType.UNIT_CREATED]: {
    oldUnit_id?: string;
    oldUnit_type?: ContentUnitType;
    oldUnit_index?: number;
    newUnit_id: string;
    newUnit_type: ContentUnitType;
    newUnit_index: number;
    isAIgenerated?: AIReviewStatus;
  };
  [GTMEventType.UNIT_DELETED]: {
    oldUnit_id: string;
    oldUnit_type: ContentUnitType;
    oldUnit_index: number;
    newUnit_id?: string;
    newUnit_type?: ContentUnitType;
    newUnit_index?: number;
    isAIgenerated?: AIReviewStatus;
  };
  [GTMEventType.UNIT_EDITED]: {
    oldUnit_id: string;
    oldUnit_type: ContentUnitType;
    oldUnit_index: number;
    newUnit_id: string;
    newUnit_type: ContentUnitType;
    newUnit_index: number;
    isAIgenerated?: AIReviewStatus;
  };
  [GTMEventType.COURSES_COPY_TRIGGERED]: {
    source_space: string;
    target_space: string;
    course_ids: string[];
    triggered_by: 'course_edit' | 'copy_courses';
  };
  // Add more event types and their payloads as needed
};

// Type helper to get the payload type for a given event type
export type EventDataForType<T extends GTMEventType> = EventPayloads[T];

/**
 * Pushes an event to GTM using sendGTMEvent from @next/third-parties/google.
 *
 * @param eventType - The type of the GTM event.
 * @param data - The event-specific data to send.
 */
export function sendGTMEvent<T extends GTMEventType>(eventType: T, data: EventDataForType<T>): void {
  const eventData = {
    event: eventType,
    [eventType]: data,
  };

  NextSendGTMEvent(eventData);
}
